import type {ReactElement} from 'react';
import {useRef} from 'react';
import {useContext} from 'react';
import {useState} from 'react';
import React from 'react';
import {DropdownDefaultSelectBox} from '@Components/dropdown-v2/components/dropdown-default-select-box';
import {ALIGNMENT_TYPE, DROPDOWN_SIZE} from '@Components/dropdown-v2/dropdown.types';
import {InputField} from '@Components/input-field-v2';
import {type InputFieldControls, InputFieldSize, InputFieldType} from '@Components/input-field-v2/input-field.types';
import {useEventWizardRegistrationDetails, useEventWizardTitle} from '@Components/event-wizard/event-wizard.hooks';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {EventDropdown} from '@Components/event-wizard/event-dropdown';
import {EventWizardRegistrationsButtonTextDropdownContent} from '@Components/event-wizard/event-wizard-registrations-button-text-dropdown-content';
import {EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT} from '@Components/event-wizard/event-wizard-container-queries-context';
import {useAppDispatch} from '@/hooks';
import {onEventWizardRegistrationChange} from '../event-wizard-slice';
import {updatePreviewRegistrationCTAText, updatePreviewRegistrationForm} from '@Components/event-wizard/preview/preview.helper';
import styles from './event-wizard-registrations-button-text.module.scss';
import {getDefaultRegistrationButtonTextOptions} from '@Components/event-wizard/event-wizard-registrations/event-wizard-registrations.types';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import useOutsideClick from '@Hooks/useOutsideClick';
import {REGISTER_BTN_TEXT_PANEL_ID} from '@Components/event-wizard/event-wizard-registrations-button-text/event-wizard-registrations-button-text.types';

export function EventWizardRegistrationsButtonText(): ReactElement<any> {
  const dispatch = useAppDispatch();
  const {isSmallScreen} = useContext(EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT);
  const onMobileModalClose = useDefaultPanelClose(REGISTER_BTN_TEXT_PANEL_ID);

  const registrationDetails = useEventWizardRegistrationDetails();
  const title = useEventWizardTitle();

  const inputFieldRef = useRef<HTMLDivElement>(null);

  const [isRegisterBtnDropdownOpen, setIsRegisterBtnDropdownOpen] = useState<boolean>(false);
  const [isTypingCustomBtnText, setIsTypingCustomBtnText] = useState<boolean>(false);
  const [customBtnText, setCustomBtnText] = useState<string>('');

  const onSubmitCustomRegisterText = (inputVal?: string): void => {
    updateSelectedRegisterText(inputVal ?? customBtnText);
  };

  const onBlurCustomBtnTextField = (inputVal: string): void => {
    setIsTypingCustomBtnText(false);
    setCustomBtnText('');
    onSubmitCustomRegisterText(inputVal)
  };

  const onInputCustomBtnText = (text: string): void => {
    setCustomBtnText(text);
  };

  const closeMobileModal = (): void => {
    if (isSmallScreen) {
      onMobileModalClose();
    }
  };

  const updateSelectedRegisterText = (text: string): void => {
    const isCustomText = !getDefaultRegistrationButtonTextOptions().includes(text);

    const updatedFields = {
      ...registrationDetails,
      registerBtnText: text,
      customBtnTexts: isCustomText ? [...(registrationDetails.customBtnTexts ?? []), text] : registrationDetails.customBtnTexts,
    };
    setCustomBtnText(isCustomText ? text : '');
    dispatch(onEventWizardRegistrationChange(updatedFields));
    updatePreviewRegistrationForm(updatedFields, title);
    updatePreviewRegistrationCTAText(text);
  };

  const getCustomBtnTextInput = (): ReactElement<any> => {
    return (
      <div className="flexbox" ref={inputFieldRef}>
        <InputField
          className={styles.inputField}
          input={customBtnText}
          size={InputFieldSize.XSMALL}
          inputType={InputFieldType.TEXT}
          maxLength={30}
          isValueBlank={true}
          onInputChange={onInputCustomBtnText}
          onInputSubmit={onSubmitCustomRegisterText}
          onInputBlur={onBlurCustomBtnTextField}
          autoFocus
        />
      </div>
    );
  };

  const getDropdown = (): ReactElement<any> => {
    return (
      <EventDropdown
        panelIdForModal={REGISTER_BTN_TEXT_PANEL_ID}
        selector={getSelector()}
        popup={
          <EventWizardRegistrationsButtonTextDropdownContent
            onClickOption={(text: string) => {
              closeMobileModal();
              updateSelectedRegisterText(text);
            }}
            onClickCustomOption={() => {
              closeMobileModal();
              setIsTypingCustomBtnText(true);
            }}
          />
        }
        popupClass={styles.popup}
        maxHeight={370}
        alignment={ALIGNMENT_TYPE.BOTTOM_START}
        open={isRegisterBtnDropdownOpen}
        modalTitle={window.i18next.t('pmwjs_button_text')}
        showModalForSmallScreen={isSmallScreen}
        doCloseOnInnerClick
        onOpen={() => {
          setIsRegisterBtnDropdownOpen(true);
        }}
        onClose={() => {
          setIsRegisterBtnDropdownOpen(false);
        }}
      />
    );
  };

  const getSelector = (): ReactElement<any> => {
    return <DropdownDefaultSelectBox className={styles.dropdownBox} size={DROPDOWN_SIZE.XSMALL} text={registrationDetails.registerBtnText} textClassName="spacing-m-r-2" />;
  };

  return (
    <div className="flex-row-align-center justify-content-between spacing-m-b-2">
      <div className="flex-row-align-center">
        <Icon className="spacing-m-r-1" icon="icon-button" size={IconSize.SIZE_ICON_16} />
        <Text val={window.i18next.t('pmwjs_button_text')} size={TextSize.XXSMALL} bold />
      </div>
      <div>
        {isTypingCustomBtnText ? getCustomBtnTextInput() : getDropdown()}
      </div>
    </div>
  );
}
